import { useId } from 'react';

/**
 * Wires a form field's ARIA relationships. Given an optional error and helper
 * text, returns stable ids and the props to spread onto the control so screen
 * readers announce validity and descriptions correctly.
 *
 *  - `aria-invalid` reflects the presence of an error.
 *  - `aria-describedby` points at the helper (`#<id>-help`) and/or error
 *    (`#<id>-err`) nodes that are present in the DOM.
 */
export interface FieldAria {
  /** Base id for the control (label `htmlFor` target). */
  id: string;
  helpId: string;
  errorId: string;
  controlProps: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby'?: string;
  };
}

export function useFieldError(params: {
  id?: string;
  hasError: boolean;
  hasHelp: boolean;
}): FieldAria {
  const generated = useId();
  const id = params.id ?? generated;
  const helpId = `${id}-help`;
  const errorId = `${id}-err`;

  const describedBy = [params.hasHelp ? helpId : null, params.hasError ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return {
    id,
    helpId,
    errorId,
    controlProps: {
      id,
      'aria-invalid': params.hasError,
      ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    },
  };
}
